import classNames from "classnames";
import { useCallback, useMemo, useRef, useState } from "react";
import { IntlShape, useIntl } from "react-intl";
import Switch from "react-switch";
import rgbHex from "rgb-hex";

import {
  ChartPeriod,
  SalesDashboardData,
  SalesRecord,
} from "../../../data-store";
import { ImpactCategory } from "../../../domain/impactCategories";
import {
  brine,
  darkTurquoiseCol,
  foodstepsTurquoiseCol,
  whiteCol,
} from "../../graphs/colors";
import MultiAxisLineChart from "../../graphs/MultiAxisLineChart/MultiAxisLineChart";
import "./ImpactCategoryChart.css";
import PercentageLozenge from "../../utils/PercentageLozenge";
import { useXAxisLabels } from "../../utils/xAxixTimeLabels";
import { useImpactCategory } from "../ImpactCategoryProvider";

import "./ImpactCategoryChart.css";
import "../SalesDashboardPage.css";

const periodKeyMap: Record<ChartPeriod, keyof SalesDashboardData> = {
  MONTHLY: "monthlyData",
  QUARTERLY: "quarterlyData",
  YEARLY: "yearlyData",
};

type ChartTooltip = {
  dataPoints?: Array<{
    datasetIndex: number;
    dataIndex: number;
    dataset?: { pointBackgroundColor?: string };
    label?: string;
  }>;
};

interface MetaData {
  growthComparedToPreviousTimePeriod?: number | null;
  percentageChange?: number | null;
  value?: number | null;
  year?: number;
}

const ImpactCategoryChart: React.FC<{
  period: "MONTHLY" | "QUARTERLY" | "YEARLY";
  data: SalesDashboardData;
}> = ({ period, data }) => {
  const { impactCategory } = useImpactCategory();
  const [showSales, setShowSales] = useState(false);

  const intl = useIntl();

  const xAxisTimeLabels = useXAxisLabels(period);

  const [selected, setSelected] = useState(null);
  const [xAxisMin] = useState(0);
  const chartRef = useRef(null);
  const [selectedLabelPos, setSelectedLabelPos] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  const UNITS = useCallback(
    (isSales) => {
      if (isSales) {
        return intl.formatMessage({
          id: "components/pages:SalesDashBoard/lineChartEmission/tooltip/unit:sales",
          defaultMessage: "products",
        });
      } else {
        if (impactCategory === "GHG") {
          return intl.formatMessage({
            id: "domain/EffectType:ghg/largeUnitString",
            defaultMessage: "t CO₂e",
          });
        }
        if (impactCategory === "LAND_USE") {
          return intl.formatMessage({
            id: "domain/EffectType:landUse/unitString",
            defaultMessage: "m²yr",
          });
        }
        if (impactCategory === "WATER_USE") {
          return intl.formatMessage({
            id: "domain/EffectType:waterUse/unitString",
            defaultMessage: "L",
          });
        }
      }
      return "";
    },
    [impactCategory, intl]
  );

  const formattedData = useMemo(() => {
    const key = periodKeyMap[period];
    const dataset: SalesRecord[] = (data?.[key] as SalesRecord[]) || [];

    let emissions: number[] = new Array(12).fill(null);
    let sales: number[] = new Array(12).fill(null);
    let emissionsMetaData: MetaData[] = new Array(12)
      .fill(null)
      .map(() => ({}));
    let salesMetaData: MetaData[] = new Array(12).fill(null).map(() => ({}));

    // Calculate total emissions & sales for the year to determine averages
    let totalEmissions = 0;
    let totalSales = 0;

    dataset.forEach((item) => {
      const index: number =
        period === "MONTHLY"
          ? (item.salesDataTimePeriod as { month: number }).month - 1
          : period === "QUARTERLY"
          ? (item.salesDataTimePeriod as { quarter: number }).quarter - 1
          : 0;

      let emissionValue = null;
      if (impactCategory === "GHG") {
        emissionValue = item.ghgEmissions ?? null;
      } else if (impactCategory === "LAND_USE") {
        emissionValue = item.landUse ?? null;
      } else if (impactCategory === "WATER_USE") {
        emissionValue = item.waterUse ?? null;
      }

      const salesValue = item.sales ?? null;

      if (emissionValue !== null) {
        totalEmissions += emissionValue;
      }
      if (salesValue !== null) {
        totalSales += salesValue;
      }

      // Store only the numeric values in `data`
      if (emissionValue) {
        emissions[index] = emissionValue;
      }
      if (salesValue) {
        sales[index] = salesValue;
      }

      // Store metadata separately
      emissionsMetaData[index] = {
        year: item.salesDataTimePeriod.year,
        value: emissionValue,
      };

      salesMetaData[index] = {
        year: item.salesDataTimePeriod.year,
        value: salesValue,
      };
    });

    // Compute percentage change and growth for emissions
    emissionsMetaData.forEach((item, i) => {
      if (item.value !== null) {
        item.percentageChange = totalEmissions
          ? ((item.value ?? 0) / totalEmissions) * 100
          : null;

        if (item.value != null && i > 0) {
          const previous = emissionsMetaData[i - 1].value;
          if (previous != null) {
            item.growthComparedToPreviousTimePeriod =
              (item.value - previous) / previous;
          } else {
            item.growthComparedToPreviousTimePeriod = null;
          }
        }
      }
    });

    // Compute percentage change and growth for sales
    salesMetaData.forEach((item, i) => {
      if (item.value !== null) {
        item.percentageChange =
          totalSales && item.value ? (item.value / totalSales) * 100 : null;

        if (item.value != null && i > 0) {
          const previous = salesMetaData[i - 1].value;
          if (previous != null) {
            item.growthComparedToPreviousTimePeriod =
              (item.value - previous) / previous;
          } else {
            item.growthComparedToPreviousTimePeriod = null;
          }
        }
      }
    });

    return {
      labels: xAxisTimeLabels,
      datasets: [
        {
          data: emissions,
          metaData: emissionsMetaData, // Separate metadata storage cause Chart.js only allows numbers for data field
          borderColor: foodstepsTurquoiseCol,
          backgroundColor: foodstepsTurquoiseCol,
          yAxisID: "y",
          yAxisLabel:
            impactCategory === "GHG"
              ? `${intl.formatMessage({
                  id: "domain/EffectType:ghg/title",
                  defaultMessage: "GHG Emissions",
                })} (${intl.formatMessage({
                  id: "domain/EffectType:ghg/largeUnitString",
                  defaultMessage: "t CO₂e",
                })})`
              : impactCategory === "LAND_USE"
              ? `${intl.formatMessage({
                  id: "domain/EffectType:landUse/title",
                  defaultMessage: "Land Use",
                })} (${intl.formatMessage({
                  id: "domain/EffectType:landUse/unitString",
                  defaultMessage: "m²yr",
                })})`
              : `${intl.formatMessage({
                  id: "domain/EffectType:waterUse/title",
                  defaultMessage: "Water Use",
                })} (${intl.formatMessage({
                  id: "domain/EffectType:waterUse/unitString",
                  defaultMessage: "L",
                })})`,
          pointRadius: 4,
          pointBackgroundColor: foodstepsTurquoiseCol,
          tension: 0,
          pointBorderColor: whiteCol,
          pointBorderWidth: 1,
          borderWidth: 2,
          pointHoverBorderColor: whiteCol,
          pointHoverRadius: 5,
          hoverBackgroundColor: foodstepsTurquoiseCol,
          hoverBorderColor: foodstepsTurquoiseCol,
        },
        {
          label: "Sales (Products Sold)",
          data: sales, // Only numbers
          metaData: salesMetaData, // Separate metadata storage
          borderColor: brine,
          backgroundColor: brine,
          yAxisID: "y1",
          yAxisLabel: intl.formatMessage({
            id: "components/pages:SalesDashBoard/lineChartEmission/YAxis/label:sales",
            defaultMessage: "Sales (Products Sold)",
          }),
          pointRadius: 4,
          pointBackgroundColor: brine,
          borderDash: [5, 5],
          tension: 0,
          pointBorderColor: whiteCol,
          pointBorderWidth: 1,
          pointHoverBorderColor: whiteCol,
          pointHoverRadius: 5,
          borderWidth: 2,
          hoverBackgroundColor: brine,
          hoverBorderColor: brine,
        },
      ],
    };
  }, [period, data, xAxisTimeLabels, impactCategory, intl]);

  const handleSelectedLabelIndex = useCallback(
    (selectedLabel, selectedPos) => {
      setSelected(
        selectedLabel == null ? selectedLabel : selectedLabel + xAxisMin
      );
      if (!selectedPos || (selectedPos.x === null && selectedPos.y === null)) {
        setSelectedLabelPos({ x: -9999, y: -9999 }); // Move off-screen initially
        return;
      }
      const rect = (chartRef.current as any)?.getBoundingClientRect();
      const { innerWidth } = window;
      if (innerWidth > rect.x + selectedPos.x + 300) {
        setSelectedLabelPos({
          x:
            formattedData.datasets[0].data[selectedLabel] ||
            formattedData.datasets?.[1]?.data?.[selectedLabel]
              ? rect.x + selectedPos.x + 10
              : rect.x + selectedPos.x - 60,
          y:
            formattedData.datasets[0].data[selectedLabel] ||
            formattedData.datasets[1]?.data?.[selectedLabel]
              ? rect.y + 30
              : rect.y + 80,
        });
      } else {
        setSelectedLabelPos({
          x:
            formattedData.datasets[0].data[selectedLabel] ||
            formattedData.datasets?.[1]?.data?.[selectedLabel]
              ? rect.x + selectedPos.x - 130
              : rect.x + selectedPos.x - 60,
          y:
            formattedData.datasets[0].data[selectedLabel] ||
            formattedData.datasets[1]?.data?.[selectedLabel]
              ? rect.y - 100
              : rect.y + 80,
        });
      }
    },
    [formattedData.datasets, xAxisMin]
  );

  const tooltipTitle = useCallback(() => {
    if (impactCategory === "GHG") {
      return intl.formatMessage({
        id: "domain/EffectType:ghg/title",
        defaultMessage: "GHG Emissions",
      });
    } else if (impactCategory === "LAND_USE") {
      return intl.formatMessage({
        id: "domain/EffectType:landUse/title",
        defaultMessage: "Land Use",
      });
    } else if (impactCategory === "WATER_USE") {
      return intl.formatMessage({
        id: "domain/EffectType:waterUse/title",
        defaultMessage: "Water Use",
      });
    }
  }, [impactCategory, intl]);

  const customTooltip = useCallback(
    (tooltip: ChartTooltip) => {
      const dp = tooltip?.dataPoints?.[0];
      if (!dp) return null;
      const { datasetIndex, dataIndex, dataset } = dp;

      const chartDataset = formattedData.datasets[datasetIndex];
      const meta = chartDataset?.metaData?.[dataIndex] ?? {};

      const dataPoints: DataPoint[] = [
        {
          value: chartDataset?.data?.[dataIndex] ?? null,
          color: dataset?.pointBackgroundColor ?? "gray",
          growthComparedToPreviousTimePeriod:
            meta.growthComparedToPreviousTimePeriod ?? null,
          percentageChange: meta.percentageChange ?? null,
          year: meta.year,
          timePeriodTitle: `${dp.label} ${meta.year}`,
          title:
            datasetIndex === 0
              ? tooltipTitle()
              : intl.formatMessage({
                  id: "components/pages:SalesDashBoard/lineChartEmission/tooltip/title:sales",
                  defaultMessage: "Sales",
                }),
          unit: UNITS(datasetIndex === 0 ? false : true),
        },
      ];

      return (
        <Tooltip
          dataPoints={dataPoints}
          impactCategory={ImpactCategory.GHG}
          intl={intl}
          isSelectedLabel={selected != null}
          period={period}
          timePeriodLabel={dp.label}
          previousTimePeriodLabel={
            dp.dataIndex > 0
              ? xAxisTimeLabels[dp.dataIndex - 1]
              : xAxisTimeLabels[dp.dataIndex]
          }
        />
      );
    },
    [
      formattedData.datasets,
      tooltipTitle,
      intl,
      UNITS,
      selected,
      period,
      xAxisTimeLabels,
    ]
  );

  return (
    <>
      <div className="EmissionSalesChart">
        {selected != null && selectedLabelPos.x && selectedLabelPos.y ? (
          <div
            style={{
              position: "absolute",
              left: selectedLabelPos.x,
              top: selectedLabelPos.y,
              zIndex: 1000,
            }}
          >
            <Tooltip
              dataPoints={formattedData.datasets
                .slice(0, showSales ? 2 : 1)
                .map((el, id) => {
                  const meta = el.metaData?.[selected] || {};
                  return {
                    value: el?.data?.[selected] ?? null,
                    color: el.backgroundColor,
                    growthComparedToPreviousTimePeriod:
                      meta.growthComparedToPreviousTimePeriod ?? null,
                    percentageChange: meta.percentageChange ?? null,
                    year: meta.year,
                    title: id === 0 ? tooltipTitle() : "Sales",
                    unit: UNITS(id === 0 ? false : true),
                  };
                })
                .filter((el) => el.value != null)}
              impactCategory={ImpactCategory.GHG}
              intl={intl}
              isSelectedLabel={selected == null ? false : true}
              period={period}
              timePeriodLabel={xAxisTimeLabels[selected]}
              previousTimePeriodLabel={
                selected > 0
                  ? xAxisTimeLabels[selected - 1]
                  : xAxisTimeLabels[selected]
              }
            />
          </div>
        ) : null}
        <div ref={chartRef}>
          <MultiAxisLineChart
            xAxisMin={xAxisMin}
            xAxisMax={12}
            data={{
              ...formattedData,
              datasets: formattedData.datasets.slice(0, showSales ? 2 : 1),
            }}
            height={320}
            maintainAspectRatio={false}
            handleSelectedLabelIndex={handleSelectedLabelIndex}
            customTooltip={customTooltip}
          />
        </div>
        <div className="d-flex sales-switch-container">
          <span className="sales-switch-text">
            {intl.formatMessage({
              id: "components/pages:SalesDashBoard/lineChartEmission/salesToggle",
              defaultMessage: "Sales",
            })}
          </span>
          <Switch
            name="switch"
            checked={showSales}
            onChange={() => {
              setShowSales((prevState) => !prevState);
            }}
            checkedIcon={false}
            width={40}
            height={24}
            onColor={"#" + rgbHex(darkTurquoiseCol).slice(0, -2)}
            uncheckedIcon={false}
            boxShadow="0px 2px 10px 0px rgba(0, 0, 0, 0.14)"
            activeBoxShadow="0px 2px 10px 0px rgba(0, 0, 0, 0.14)"
            className={classNames("sales-switch", {
              "sales-switch-checked": showSales,
            })}
            offColor="#DEE2E6"
            handleDiameter={18}
          />
        </div>
      </div>
    </>
  );
};

interface DataPoint {
  value: number | null;
  color: string;
  growthComparedToPreviousTimePeriod: number | null;
  percentageChange: number | null;
  year?: number;
  title?: string;
  timePeriodTitle?: string;
  unit?: string;
}
interface TooltipProps {
  dataPoints: Array<DataPoint>;
  impactCategory: ImpactCategory;
  intl: IntlShape;
  isSelectedLabel?: boolean;
  period?: "MONTHLY" | "QUARTERLY" | "YEARLY";
  timePeriodLabel?: string;
  previousTimePeriodLabel?: string | null;
}

export function Tooltip(props: TooltipProps) {
  const { dataPoints } = props;

  const NO_DATA_TEXT = useMemo(() => {
    return props.intl.formatMessage(
      {
        id: "components/pages:SalesDashBoard/lineChartEmission/tooltip/title/noData",
        defaultMessage: "No data for {timePeriodLabel}",
      },
      {
        timePeriodLabel: props.timePeriodLabel,
      }
    );
  }, [props.intl, props.timePeriodLabel]);

  const SINCE_PREVIOUS_TEXT = useMemo(() => {
    if (!props.previousTimePeriodLabel) return "";
    return props.intl.formatMessage(
      {
        id: "components/pages:SalesDashBoard/lineChartEmission/tooltip/sincePrevious",
        defaultMessage: "since {period}",
      },
      { period: props.previousTimePeriodLabel }
    );
  }, [props.intl, props.previousTimePeriodLabel]);

  if (dataPoints.every((d) => d.value == null)) {
    return <div className="SalesChart_Tooltip_no_data">{NO_DATA_TEXT}</div>;
  }

  return (
    <div className="EmissionSalesChart_Tooltip">
      <div>
        {dataPoints.map((datapoint, id) => {
          return (
            <div key={id}>
              <div className="d-flex EmissionSalesChart_Tooltip_container">
                {datapoint.timePeriodTitle && (
                  <div className="d-flex EmissionSalesChart_Tooltip_header">
                    <div className="SalesChart_Tooltip_title">
                      {datapoint.timePeriodTitle}
                    </div>
                  </div>
                )}
                <div className="d-flex EmissionSalesChart_Tooltip_header">
                  <div className="SalesChart_Tooltip_title">
                    {datapoint.title}
                  </div>
                  <div
                    style={{ background: datapoint.color }}
                    className="EmissionSalesChart_Tooltip_color"
                  ></div>
                </div>
                <div className="d-flex EmissionSalesChart_Tooltip_body">
                  <div className="SalesChart_Tooltip_column_info">
                    <div className="SalesChart_Tooltip_title">
                      {datapoint.value?.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div className="SalesChart_Tooltip_description">
                      {datapoint.unit}
                    </div>
                  </div>
                  <div className="SalesChart_Tooltip_column_info">
                    <div className="SalesChart_Tooltip_title">
                      {datapoint.percentageChange?.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                      %
                    </div>
                    <div className="SalesChart_Tooltip_description">
                      {props.intl.formatMessage(
                        {
                          id: "components/pages:SalesDashBoard/lineChartEmission/tooltip/ofYear",
                          defaultMessage: "of {year}",
                        },
                        { year: datapoint.year }
                      )}
                    </div>
                  </div>
                  {props.period !== "YEARLY" && (
                    <div
                      style={{ flexGrow: 1, width: "fit-content" }}
                      className="SalesChart_Tooltip_column_info"
                    >
                      <div className="SalesChart_Tooltip_title">
                        <PercentageLozenge
                          amount={
                            datapoint.growthComparedToPreviousTimePeriod ?? 0
                          }
                          intl={props.intl}
                          sentiment="neutral"
                        />
                      </div>
                      <div className="SalesChart_Tooltip_description">
                        {SINCE_PREVIOUS_TEXT}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {id !== dataPoints.length - 1 && (
                <div className="SalesChart_Tooltip_divider" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ImpactCategoryChart;
